import { Injectable } from '@angular/core';
import { BaseUserDataService } from './base-user-data.service';
import { Router } from '@angular/router';
import { BaseService } from '../base-service/base.service';
import { AuthenticationService } from '../authentication/authentication.service';
import { ConfirmDialogService } from '../../dialogs/confirm/confirm-dialog.service';
import { DialogButton } from '../../enums/dialog-button.enum';
import { LoginRequest } from '../../models/authentication/login-request.model';
import { BaseUser } from '../../models/base-user/base-user.model';
import { BasePasswordResetRequest } from '../../models/base-user/base-password-reset-request.model';
import { BaseNewUserRequest } from '../../models/base-user/base-new-user-request.model';
import { ValidatePasswordResetTokenResponse } from '../../models/base-user/validate-password-reset-token-response.model';
import { Secure } from '../../enums/secure.enum';
import { PasswordReset } from '../../models/base-user/password-reset.model';
import { GetOptions } from '../../models/args/get-options.model';
import { AuthenticationDetails } from '../../models/authentication/authentication-details.model';
import { ValidateVerifyEmailTokenResponse } from '../../models/base-user/validate-verify-email-token-response.model';

/**
 * The base user service
 */
@Injectable({
    providedIn: 'root'
})
export class BaseUserService extends BaseService {
    // **********************
    // * Public properties
    // **********************

    /** Get accessor for the user ID */
    public get userId(): string {
        if (!this._userId) {
            this._userId = this.authenticationService.getUserId();
        }
        return this._userId;
    }

    /** Get accessor for user role */
    public get userRole(): string {
        if (!this._primaryUserRole) {
            this._primaryUserRole = this.authenticationService.getPrimaryUserRole();
        }
        return this._primaryUserRole;
    }

    // **********************
    // * Protected properties
    // **********************

    /** The user ID */
    protected _userId: string;

    /** The user's primary role */
    protected _primaryUserRole: string;

    /**
     * Constructor
     * @param authenticationService - A reference to the authentication service
     * @param dataService - A reference to the user data service
     * @param confirmDialogService - A reference to the confirm dialog service
     * @param router - A reference to the router
     */
    constructor(
        protected authenticationService: AuthenticationService,
        protected dataService: BaseUserDataService,
        protected confirmDialogService: ConfirmDialogService,
        protected router: Router
    ) {
        super();
    }

    // **********************
    // * Public methods
    // **********************

    /**
     * Delete a user
     * @param userId - The ID of the user to delete
     * @returns True if the delete succeeded
     * @async
     */
    public async _deleteUser(userId: string): Promise<boolean> {
        const confirmStatus = await this.confirmDialogService.openConfirmDialog(
            'Delete User',
            'Do you want to delete the selected user? This action cannot be undone.'
        );
        if (confirmStatus.button === DialogButton.Cancel) {
            return false;
        }
        const result = await this.dataService._deleteUser(userId);
        return result;
    }

    /**
     * Get all admin users
     * @returns An array of admin users
     * @async
     */
    public async _getAdminUsers<T extends BaseUser>(): Promise<T[]> {
        const users = await this.dataService._getAdminUsers<T>();
        return users;
    }

    /**
     * Get all basic users
     * @returns An array of basic (non-admin) users
     * @async
     */
    public async _getBasicUsers<T extends BaseUser>(): Promise<T[]> {
        const users = await this.dataService._getBasicUsers<T>();
        return users;
    }

    /**
     * Get the logged in user
     * @returns The user object
     * @async
     */
    public async _getCurrentUser<T extends BaseUser>(): Promise<T> {
        if (!this.userId) {
            return null;
        }
        const id = this.userId;
        const user = await this.dataService._getUser<T>(id);
        return user;
    }

    /**
     * Get all users
     * @returns An array of users
     * @async
     */
    public async _getUsers<T extends BaseUser>(
        options: GetOptions
    ): Promise<T[]> {
        const users = await this.dataService._getUsers<T>(options);
        return users;
    }

    /**
     * Get the user's primary role
     * @returns The user's primary role
     * @async
     */
    public _getPrimaryUserRole(): string {
        return this.authenticationService.getPrimaryUserRole();
    }

    /**
     * Get the user
     * @param userId - ID of the user to get
     * @returns The user object
     * @async
     */
    public async _getUser<T extends BaseUser>(userID: string): Promise<T> {
        const user = await this.dataService._getUser<T>(userID);
        return user;
    }

    /**
     * Login a user
     * @param user - The login request
     * @returns True if the login succeeded
     * @async
     */
    public async _loginUser(user: LoginRequest): Promise<AuthenticationDetails> {
        const authenticationDetails = await this.authenticationService.authenticate(user);
        if (authenticationDetails.authenticated === true) {
            this._userId = this.authenticationService.getUserId();
            this._primaryUserRole = this.authenticationService.getPrimaryUserRole();
        }
        return authenticationDetails;
    }

    /**
     * Logout the user
     * @param redirectRoute - The route to redirect to after logging out
     */
    public _logoutUser(redirectRoute: string = ''): void {
        this._userId = null;
        this.authenticationService.logoutUser();
        if (redirectRoute) {
            this.router.navigate([redirectRoute]);
        }
    }

    /**
     * Register a new user
     * @param newUser - The new user request object
     * @param secure - An enum indicating whether the API endpoint is secure
     * @returs The new user
     * @async
     */
    public async _registerUser<T extends BaseNewUserRequest, U extends BaseUser>(
        newUser: T,
        secure: Secure = Secure.false
    ): Promise<U> {
        const user = await this.authenticationService.registerUser<T, U>(
            newUser,
            secure
        );
        // if (user) {
        //   this._userId = this.authenticationService.getUserId();
        //   this._primaryUserRole = this.authenticationService.getPrimaryUserRole();
        // }
        return user;
    }

    /**
     * Request a password reset
     * @param email - The email address of the user requesting the password reset
     * @returns true if the request succeeded
     * @async
     */
    public async _requestPasswordReset(email: string): Promise<boolean> {
        const passwordResetRequest = new BasePasswordResetRequest(email);
        const result = await this.dataService._requestPasswordReset(
            passwordResetRequest
        );
        return result;
    }

    /**
     * Request a password reset token. This method is only used for testing.
     * @param email - The email address of the user requesting the password reset
     * @returns The password reset token
     * @async
     */
    public async _requestPasswordResetToken(email: string): Promise<string> {
        const passwordResetRequest = new BasePasswordResetRequest(email);
        const result = await this.dataService._requestPasswordResetToken(
            passwordResetRequest
        );
        return result;
    }

    public async _resendVerificationEmail(email: string): Promise<boolean> {
        return await this.dataService._resendVerificationEmail(
            email
        );
    }

    /**
     * Reset a user password
     * @param token - The password reset token
     * @param userId - The ID of the user
     * @param newPassword - The user's new password
     * @returns True if the password reset succeeded
     * @async
     */
    public async _resetPassword(
        token: string,
        userId: string,
        newPassword: string
    ): Promise<boolean> {
        const resetRequest = new PasswordReset(token, userId, newPassword);
        const result = this.dataService._resetPassword(resetRequest);
        return result;
    }

    /**
     * Update a user
     * @param user - The user to update
     * @returns The updated user
     * @async
     */
    public async _updateUser<T extends BaseUser>(user: T): Promise<T> {
        const result = await this.dataService._updateUser(user);
        return result;
    }

    /**
     * Validate a password reset token
     * @param passwordResetToken - The password reset token
     * @returns A validate password reset token response object
     * @async
     */
    public async _validatePasswordResetToken(
        passwordResetToken: string
    ): Promise<ValidatePasswordResetTokenResponse> {
        const result = await this.dataService._validatePasswordResetToken(
            passwordResetToken
        );
        return result;
    }

    /**
     * Validate a verify email token
     * @param verifyEmailToken - The verify email token
     * @returns A validate verify email token response object
     */
    public async _validateVerifyEmailToken(
        verifyEmailToken: string
    ): Promise<ValidateVerifyEmailTokenResponse> {
        const result = await this.dataService._validateVerifyEmailToken(
            verifyEmailToken
        );
        return result;
    }
}
